import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { buildApiUrl } from '../config/apiBaseUrl'

// Promesa de refresh compartida (single-flight). El backend ROTA el refresh
// token en cada uso (invalida el anterior), por lo que si varias peticiones
// reciben 401 a la vez y cada una pide refresh con el mismo token viejo, solo
// la primera tiene éxito y las demás fallan → logout espurio y "se borra" la
// sesión. Compartiendo una única promesa, todas esperan el mismo refresh.
let refreshPromise = null

const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      setUser: (user) => set({ user, isAuthenticated: !!user }),
      
      setTokens: (accessToken, refreshToken) => set({ 
        accessToken, 
        refreshToken 
      }),
      
      setLoading: (isLoading) => set({ isLoading }),
      
      setError: (error) => set({ error }),
      
      login: async (usuario, password, modulo) => {
        set({ isLoading: true, error: null })
        
        try {
          const response = await fetch(buildApiUrl('/auth/login'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ usuario, password, modulo })
          })
          
          const rawBody = await response.text()
          let data = null

          try {
            data = rawBody ? JSON.parse(rawBody) : null
          } catch (parseError) {
            data = null
          }

          if (!response.ok) {
            if (response.status === 502 || rawBody.includes('Bad Gateway')) {
              throw new Error('Servicio no disponible. Intente nuevamente en unos minutos.')
            }
            throw new Error(data?.error || 'Error al iniciar sesión')
          }


          if (data?.success === false) {
            throw new Error(data?.error || 'Error al iniciar sesión')
          }

          if (!data?.data?.user || !data?.data?.tokens) {
            throw new Error('Respuesta inválida del servidor')
          }
          
          set({
            user: data.data.user,
            accessToken: data.data.tokens.accessToken,
            refreshToken: data.data.tokens.refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null
          })
          
          return { success: true }
        } catch (error) {
          set({ 
            isLoading: false, 
            error: error.message,
            isAuthenticated: false 
          })
          return { success: false, error: error.message }
        }
      },
      
      logout: async () => {
        const { accessToken } = get()
        
        if (accessToken) {
          try {
            await fetch(buildApiUrl('/auth/logout'), {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
              }
            })
          } catch (error) {
            console.error('Logout error:', error)
          }
        }
        
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null
        })
      },
      
      refreshAccessToken: async () => {
        // Si ya hay un refresh en vuelo, todas las peticiones que reciban 401
        // esperan la MISMA promesa en lugar de disparar refresh en paralelo
        // (evita el race de rotación del refresh token y el logout espurio).
        if (refreshPromise) {
          return refreshPromise
        }

        refreshPromise = (async () => {
          const { refreshToken } = get()

          if (!refreshToken) {
            return false
          }

          try {
            const response = await fetch(buildApiUrl('/auth/refresh'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ refreshToken })
            })

            if (!response.ok) {
              throw new Error('Token refresh failed')
            }

            const data = await response.json()

            set({
              accessToken: data.data.accessToken,
              refreshToken: data.data.refreshToken
            })

            return true
          } catch (error) {
            console.error('Token refresh error:', error)
            get().logout()
            return false
          } finally {
            refreshPromise = null
          }
        })()

        return refreshPromise
      },
      
      clearError: () => set({ error: null })
    }),
    {
      name: 'sanroque-auth',
      partialize: (state) => ({ 
        user: state.user, 
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated 
      })
    }
  )
)

export default useAuthStore
